import { z } from 'zod';
import { NonEmptyStringSchema, TimestampSchema } from './common';

export const PAGE_SWIPE_RECORD_VERSION = '2026-06-24';
export const PAGE_SWIPE_RECORD_PRODUCER = 'tss';

export const ANALYSIS_RUN_CHAT_VERSION = '2026-06-29';
export const ANALYSIS_RUN_CHAT_PRODUCER = 'tss';

export const ANALYSIS_RUN_CHAT_ROLES = ['user', 'assistant'] as const;

export const ANALYSIS_RUN_CHAT_STATUSES = [
  'grounded',
  'degraded',
] as const;

export const ANALYSIS_RUN_CHAT_ARTIFACT_TYPES = [
  'funnel_overview',
  'recommendations',
  'page_swipe_record',
] as const;

export const AnalysisRunChatRoleEnum = z.enum(ANALYSIS_RUN_CHAT_ROLES);
export const AnalysisRunChatStatusEnum = z.enum(
  ANALYSIS_RUN_CHAT_STATUSES,
);
export const AnalysisRunChatArtifactTypeEnum = z.enum(
  ANALYSIS_RUN_CHAT_ARTIFACT_TYPES,
);

export const PageSwipeScreenshotRefSchema = z
  .object({
    path: NonEmptyStringSchema,
    viewport: NonEmptyStringSchema,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/iu),
  })
  .strict();

export const PageSwipeComponentSchema = z
  .object({
    id: NonEmptyStringSchema,
    type: NonEmptyStringSchema,
    selector: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    evidenceRefs: z.array(NonEmptyStringSchema),
    tags: z.array(NonEmptyStringSchema),
  })
  .strict();

export const PageSwipeLayoutSectionSchema = z
  .object({
    id: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    pageType: NonEmptyStringSchema,
    sourceUrl: NonEmptyStringSchema,
    componentIds: z.array(NonEmptyStringSchema),
    screenshotRefs: z.array(NonEmptyStringSchema),
    evidenceRefs: z.array(NonEmptyStringSchema),
  })
  .strict();

export const PageSwipeRecordSchema = z
  .object({
    version: z.literal(PAGE_SWIPE_RECORD_VERSION),
    producer: z.literal(PAGE_SWIPE_RECORD_PRODUCER),
    id: NonEmptyStringSchema,
    analysisRunId: NonEmptyStringSchema,
    sourceUrl: NonEmptyStringSchema,
    capturedAt: TimestampSchema,
    pageType: NonEmptyStringSchema,
    layoutSections: z.array(PageSwipeLayoutSectionSchema),
    components: z.array(PageSwipeComponentSchema),
    screenshotRefs: z.array(PageSwipeScreenshotRefSchema),
    tags: z.array(NonEmptyStringSchema),
    diagnostics: z.array(NonEmptyStringSchema),
  })
  .strict();

export const AnalysisRunChatMessageSchema = z
  .object({
    role: AnalysisRunChatRoleEnum,
    content: NonEmptyStringSchema,
  })
  .strict();

export const ChatTurnSchema = AnalysisRunChatMessageSchema;

export const AnalysisRunChatRequestSchema = z
  .object({
    messages: z.array(AnalysisRunChatMessageSchema),
  })
  .strict();

export const AnalysisRunChatCitationSchema = z
  .object({
    artifactPath: NonEmptyStringSchema,
    artifactType: AnalysisRunChatArtifactTypeEnum,
    evidenceRefs: z.array(NonEmptyStringSchema),
    reason: NonEmptyStringSchema,
  })
  .strict();

export const AnalysisRunChatGapSchema = z
  .object({
    field: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    evidenceRefs: z.array(NonEmptyStringSchema).optional(),
  })
  .strict();

export const AnalysisRunChatGuardrailsSchema = z
  .object({
    groundedInAnalysisRunArtifacts: z.literal(true),
    noExternalKnowledge: z.literal(true),
    noLiveLlmCall: z.literal(true),
  })
  .strict();

export const AnalysisRunChatResponseSchema = z
  .object({
    version: z.literal(ANALYSIS_RUN_CHAT_VERSION),
    producer: z.literal(ANALYSIS_RUN_CHAT_PRODUCER),
    analysisRunId: NonEmptyStringSchema,
    status: AnalysisRunChatStatusEnum,
    reply: NonEmptyStringSchema,
    citations: z.array(AnalysisRunChatCitationSchema),
    gaps: z.array(AnalysisRunChatGapSchema),
    guardrails: AnalysisRunChatGuardrailsSchema,
  })
  .strict();

export type PageSwipeScreenshotRef = z.infer<
  typeof PageSwipeScreenshotRefSchema
>;
export type PageSwipeComponent = z.infer<typeof PageSwipeComponentSchema>;
export type PageSwipeLayoutSection = z.infer<
  typeof PageSwipeLayoutSectionSchema
>;
export type PageSwipeRecord = z.infer<typeof PageSwipeRecordSchema>;

export type AnalysisRunChatRole = (typeof ANALYSIS_RUN_CHAT_ROLES)[number];
export type AnalysisRunChatStatus =
  (typeof ANALYSIS_RUN_CHAT_STATUSES)[number];
export type AnalysisRunChatArtifactType =
  (typeof ANALYSIS_RUN_CHAT_ARTIFACT_TYPES)[number];
export type AnalysisRunChatMessage = z.infer<
  typeof AnalysisRunChatMessageSchema
>;
export type ChatTurn = AnalysisRunChatMessage;
export type AnalysisRunChatRequest = z.infer<
  typeof AnalysisRunChatRequestSchema
>;
export type AnalysisRunChatCitation = z.infer<
  typeof AnalysisRunChatCitationSchema
>;
export type AnalysisRunChatGap = z.infer<typeof AnalysisRunChatGapSchema>;
export type AnalysisRunChatGuardrails = z.infer<
  typeof AnalysisRunChatGuardrailsSchema
>;
export type AnalysisRunChatResponse = z.infer<
  typeof AnalysisRunChatResponseSchema
>;
